import { useEffect, useRef, useState } from "react"
import mammoth from "mammoth"
import { useTranslation } from "react-i18next"

// Helper function to fetch file as ArrayBuffer with better CORS handling
const fetchFileAsArrayBuffer = async (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'arraybuffer'
    
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response)
      } else {
        reject(new Error(`Failed to fetch file: ${xhr.statusText}`))
      }
    }
    
    xhr.onerror = () => {
      reject(new Error('Network error while fetching file'))
    }
    
    xhr.send()
  })
}

// Simple in-memory cache to avoid re-conversion on the same session
const docxHtmlCache = new Map()

const getStableKeyForFile = (file) => {
  if (!file) return null
  if (typeof file === 'string') return file
  if (file && file.storageUrl) return file.storageUrl
  if (file && file.name) return `file-name:${file.name}`
  return null
}

const DOCXPreview = ({ file, isMobile = false, embed = false }) => {
  const contentRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
const { t } = useTranslation()

  useEffect(() => {
    if (!file) {
      if (contentRef.current) contentRef.current.innerHTML = ""
      setIsLoading(false)
      return
    }

    const loadDocxContent = async () => {
      try {
        setIsLoading(true)
        const cacheKey = getStableKeyForFile(file)

        // Fast path: use in-memory cache if available
        if (cacheKey && docxHtmlCache.has(cacheKey)) {
          if (contentRef.current) contentRef.current.innerHTML = docxHtmlCache.get(cacheKey)
          setIsLoading(false)
          return
        }

        // Secondary fast path: check sessionStorage (survives page reloads within session)
        if (cacheKey) {
          const cachedHtml = sessionStorage.getItem(`docx-html:${cacheKey}`)
          if (cachedHtml) {
            docxHtmlCache.set(cacheKey, cachedHtml)
            if (contentRef.current) contentRef.current.innerHTML = cachedHtml
            setIsLoading(false)
            return
          }
        }

        let arrayBuffer

        // Check if file is a document object from database or a File/Blob object
        if (file && file.storageUrl) {
          // File from database - storageUrl is already a public URL
          arrayBuffer = await fetchFileAsArrayBuffer(file.storageUrl)
        } else if (typeof file === 'string') {
          // Direct URL string
          arrayBuffer = await fetchFileAsArrayBuffer(file)
        } else if (file instanceof File || file instanceof Blob) {
          // File object from file input
          const reader = new FileReader()
          arrayBuffer = await new Promise((resolve, reject) => {
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = reject
            reader.readAsArrayBuffer(file)
          })
        } else {
          throw new Error('Invalid file format')
        }

        const result = await mammoth.convertToHtml({ arrayBuffer })
        if (contentRef.current) contentRef.current.innerHTML = result.value
        if (cacheKey) {
          docxHtmlCache.set(cacheKey, result.value)
          try {
            sessionStorage.setItem(`docx-html:${cacheKey}`, result.value)
          } catch (_) {
            // Ignore storage quota errors silently
          }
        }
      } catch (err) {
        console.error("DOCX render error:", err)
        if (contentRef.current) {
          contentRef.current.innerHTML = `
            <div class="text-center text-red-500 p-8">
              <p>Error loading document: ${err.message}</p>
            </div>
          `
        }
      } finally {
        setIsLoading(false)
      }
    }

    loadDocxContent()
  }, [file])

  return !!file ? (
    <div className="w-full  rounded-3xl overflow-hidden flex flex-col" style={{ height: isMobile ? 'auto' : '68vh', maxHeight: isMobile ? 'auto' : '680px' }}>
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2 text-gray-600">{t("loader.downloadingFile")}</span>
        </div>
      )}
      <div className="flex-1 overflow-auto">
        <div className="max-w-[900px] mx-auto ">
          <div
            ref={contentRef}
            className="prose prose-neutral max-w-none p-8"
          />
        </div>
      </div>
    </div>
  ) : null
}

export default DOCXPreview
